// PWA — 提交表单状态 Hook（Req 7.1, 7.2, 7.3, 7.4, 6.4 / 任务 9.4）
//
// 职责：
//  - 维护受控表单状态（提示词、尺寸、steps、guidance、seed/随机）。
//  - 尺寸输入经 shared 的 normalizeTo16（任务 2.1）实时给出规范化提示（Req 7.3, 7.4）。
//  - 提交前复用 submitGate（canSubmit，任务 2.21）门控：status≠running 时提示先启动
//    且不发起请求（Req 6.4）；否则 `POST /jobs` 并回传 jobId/status（Req 7.1, 7.2）。
//
// 认证由上层传入的 authedFetch 承载（自动附带 JWT）；全部为出站请求，符合零入站架构。

import { useCallback, useMemo, useState } from "react";
import type { GenerationRequest, PowerState } from "@comfy/shared";
import {
  buildRequest,
  defaultFormValues,
  deriveSubmitAvailability,
  normalizeDimension,
  type DimNormalization,
  type SubmitAvailability,
  type SubmitFormValues,
} from "./submitView.js";

/** authedFetch 的最小签名（与 useAuth / usePower 暴露的一致）。 */
export type AuthedFetch = (input: string, init?: RequestInit) => Promise<Response>;

/** 提交成功后的返回体（对应 submit Lambda 的 202 响应）。 */
export interface SubmitResult {
  jobId: string;
  status: string;
}

export interface UseSubmitOptions {
  /** 带认证的 fetch（自动附带 Bearer token）。 */
  authedFetch: AuthedFetch;
  /** 当前电源状态（来自 usePower，用于提交门控）。 */
  powerState: PowerState;
  /** 是否已认证：未认证时不允许提交。 */
  authenticated: boolean;
  /** 提交成功回调（如写入最近列表，任务 9.7）。 */
  onSubmitted?: (jobId: string, request: GenerationRequest) => void;
}

export interface UseSubmitResult {
  /** 当前表单值。 */
  values: SubmitFormValues;
  /** 更新单个字段。 */
  setField: <K extends keyof SubmitFormValues>(
    key: K,
    value: SubmitFormValues[K],
  ) => void;
  /** 宽度规范化提示（非 16 倍数时非 null）。 */
  widthHint: DimNormalization;
  /** 高度规范化提示（非 16 倍数时非 null）。 */
  heightHint: DimNormalization;
  /** 可提交性判定（含门控原因）。 */
  availability: SubmitAvailability;
  /** 提交进行中。 */
  submitting: boolean;
  /** 最近一次错误信息。 */
  error: string | null;
  /** 最近一次提交成功结果（jobId/status）。 */
  result: SubmitResult | null;
  /** 生成一个随机 seed 并切换为固定模式（便于复现）。 */
  randomizeSeed: () => void;
  /** 触发提交（`POST /jobs`）。 */
  submit: () => void;
  /** 重置结果与错误（用于开始新的一次提交）。 */
  reset: () => void;
}

/** 32 位随机 seed（用于「随机」按钮生成可复现的固定值）。 */
function randomSeed(): number {
  return Math.floor(Math.random() * 0xffffffff);
}

export function useSubmit(opts: UseSubmitOptions): UseSubmitResult {
  const { authedFetch, powerState, authenticated, onSubmitted } = opts;

  const [values, setValues] = useState<SubmitFormValues>(defaultFormValues);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);

  const setField = useCallback(
    <K extends keyof SubmitFormValues>(key: K, value: SubmitFormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // 尺寸规范化提示（Req 7.4）：实时反映当前输入是否会被调整到 16 倍数。
  const widthHint = useMemo(
    () => normalizeDimension(values.width),
    [values.width],
  );
  const heightHint = useMemo(
    () => normalizeDimension(values.height),
    [values.height],
  );

  // 可提交性：门控（powerState）+ 提示词是否填写；未认证一律不可提交。
  const availability = useMemo<SubmitAvailability>(() => {
    if (!authenticated) {
      return { canSubmit: false, reason: "请先登录" };
    }
    return deriveSubmitAvailability(
      powerState,
      values.positivePrompt.trim() !== "",
    );
  }, [authenticated, powerState, values.positivePrompt]);

  const randomizeSeed = useCallback(() => {
    setValues((prev) => ({ ...prev, seedMode: "fixed", seed: randomSeed() }));
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  const submit = useCallback(() => {
    // 提交门控（Req 6.4）：不满足条件直接提示，不发起请求。
    if (!availability.canSubmit) {
      setError(availability.reason);
      return;
    }
    if (submitting) return;

    const request = buildRequest(values);
    setSubmitting(true);
    setError(null);
    setResult(null);

    authedFetch("/jobs", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => null)) as
          | (Partial<SubmitResult> & { message?: string })
          | null;
        if (res.ok && body?.jobId) {
          const submitted: SubmitResult = {
            jobId: body.jobId,
            status: body.status ?? "queued",
          };
          setResult(submitted);
          onSubmitted?.(submitted.jobId, request);
          return;
        }
        // 409 门控（服务端二次判定）或其他失败：透传可读信息。
        throw new Error(body?.message ?? `提交失败（${res.status}）`);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [availability, submitting, values, authedFetch, onSubmitted]);

  return {
    values,
    setField,
    widthHint,
    heightHint,
    availability,
    submitting,
    error,
    result,
    randomizeSeed,
    submit,
    reset,
  };
}
